import React from 'react';
import { useTranslation } from 'react-i18next';

import { getUserLocale } from 'i18n/getUserLocale';
import type { TDates } from '../../types';
import { getStartDateTime } from '../../utils';
import { TimeZone } from '../TimeZone';
import { AppointmentDetails, CancelButton, Status } from './components';
import type { SessionDetailsProps } from './types';
import { StyledCounsellorProfile, StyledPill } from './styles';

const getFormattedDate = (
  dates?: TDates & { timezone?: string },
  locale?: string,
) => {
  if (!dates?.startDate || !locale) return undefined;

  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: dates?.timezone || 'UTC',
  }).format(new Date(dates.startDate));
};

export const SessionDetails = (props: SessionDetailsProps) => {
  const { appointment, canCancel, counsellor, dates, type, trailingContent } =
    props;

  const { t } = useTranslation('book');
  const locale = getUserLocale();

  const startDateTime = getStartDateTime(dates);
  const formattedDate = getFormattedDate(dates, locale);
  const formattedDateTime =
    startDateTime &&
    new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: dates?.timezone || 'UTC',
    }).format(startDateTime);

  // To keep tab order logical, we render the cancel button at the top or bottom of the component
  const cancelButton = (
    <CancelButton
      details={{
        appointmentId: appointment?.id || '',
        date: formattedDateTime || '',
        counsellor:
          counsellor?.name ||
          t('service.form.confirmDetails.counsellorName.fallback.default'),
      }}
      startDate={dates?.startDate}
      type={type}
      onCancel={appointment?.onCancel}
    />
  );

  return (
    <>
      <StyledPill data-testid="service-type" className="pill">
        {typeof type === 'string' ? t('therapyAndCoaching.pill') : type.name}
      </StyledPill>

      {canCancel?.button === 'top' && cancelButton}

      <StyledCounsellorProfile
        title={formattedDateTime || formattedDate || t('sessionDetails.title')}
        className="counsellor-profile"
        content={
          counsellor?.name
            ? t('service.form.confirmDetails.counsellorName.text', {
                counsellor: counsellor?.name,
              })
            : undefined
        }
        picture={counsellor?.picture || undefined}
        variant="small"
      />
      {appointment?.timeZone && (
        <TimeZone
          tz={appointment.timeZone}
          date={dates?.startDate || new Date()}
        />
      )}
      <AppointmentDetails
        {...props}
        dates={{ ...dates, startDate: startDateTime }}
      />
      {canCancel?.button === 'bottom' && cancelButton}

      <Status
        counsellor={counsellor}
        type={type}
        sessionPrep={appointment?.session?.sessionPrep}
      />

      {trailingContent}
    </>
  );
};
